import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Lock, Loader2, MessageCircle, Mail, PauseCircle } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { BRAND, money, whatsappHref } from '@/data/constants';
import { track } from '@/lib/tracking';
import { SINGLE_LEAD_PRICE } from '@/lib/singleLead';

/**
 * HOSTED $29 CHECKOUT HAND-OFF.
 *
 * Shown when the server says the in-page card form cannot take money (or the
 * card form just failed). The buyer is redirected AUTOMATICALLY after a short
 * countdown, in the SAME tab (`location.assign` is never popup-blocked on
 * mobile). A big button lets them go immediately, and "Stay here" cancels.
 *
 * The selected lead is remembered locally so /payment-complete can bring them
 * straight back to THAT project with their new credit.
 */

export const HOSTED_LEAD_KEY = 'gg_hosted_lead';

export const rememberHostedLead = (slug: string, listingId: string) => {
  try {
    localStorage.setItem(HOSTED_LEAD_KEY, JSON.stringify({ slug, listingId, at: Date.now() }));
  } catch {
    /* private mode */
  }
};

export const readHostedLead = (): { slug: string; listingId: string } | null => {
  try {
    const raw = localStorage.getItem(HOSTED_LEAD_KEY);
    if (!raw) return null;
    const t = JSON.parse(raw);
    if (!t?.slug || Date.now() - (t.at || 0) > 6 * 3600 * 1000) return null;
    return { slug: String(t.slug), listingId: String(t.listingId || '') };
  } catch {
    return null;
  }
};

interface Props {
  url: string;
  listingId: string;
  slug: string;
  /** Seconds before the automatic redirect. */
  seconds?: number;
}

const HostedLeadCheckout: React.FC<Props> = ({ url, listingId, slug, seconds = 4 }) => {
  const { lang } = useI18n();
  const es = lang === 'es';
  const [left, setLeft] = useState(seconds);
  const [paused, setPaused] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const gone = useRef(false);

  const go = (how: 'auto' | 'tap') => {
    if (gone.current) return;
    gone.current = true;
    setLeaving(true);
    rememberHostedLead(slug, listingId);
    track('cta_click', { action: 'hosted_single_checkout', how, listing: listingId });
    window.location.assign(url);
  };

  useEffect(() => {
    if (paused || leaving) return;
    if (left <= 0) {
      go('auto');
      return;
    }
    const t = window.setTimeout(() => setLeft((s) => s - 1), 1000);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [left, paused, leaving]);

  return (
    <div role="status" className="rounded-xl border-2 border-emerald-300 bg-emerald-50 px-4 py-4 text-sm text-emerald-950">
      <p className="flex items-center gap-2 font-extrabold">
        <Lock className="h-4 w-4" />
        {es ? `Pago seguro de ${money(SINGLE_LEAD_PRICE)} listo` : `Secure ${money(SINGLE_LEAD_PRICE)} checkout ready`}
      </p>
      <p className="mt-1.5 leading-relaxed text-emerald-900">
        {es
          ? 'Te llevamos a nuestra página de pago segura. Al terminar volverás aquí y tu crédito de lead se aplicará automáticamente para desbloquear este proyecto.'
          : 'We are taking you to our secure checkout page. When you finish you come straight back here and your lead credit is applied automatically to unlock this project.'}
      </p>

      <a
        href={url}
        onClick={(e) => {
          e.preventDefault();
          go('tap');
        }}
        style={{ touchAction: 'manipulation' }}
        className="mt-4 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-3.5 text-base font-bold text-white hover:bg-orange-600 focus:outline-none focus-visible:ring-4 focus-visible:ring-orange-300"
      >
        {leaving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Lock className="h-5 w-5" />}
        {leaving
          ? es ? 'Abriendo pago seguro…' : 'Opening secure checkout…'
          : `${es ? 'Pagar ahora' : 'Pay now'} — ${money(SINGLE_LEAD_PRICE)}`}
        {!leaving && <ArrowRight className="h-4 w-4" />}
      </a>

      <div aria-live="polite" className="mt-2 flex min-h-[20px] items-center justify-center gap-3 text-xs font-semibold text-emerald-800">
        {!paused && !leaving && (
          <>
            <span>{es ? `Redirigiendo en ${left}s…` : `Redirecting in ${left}s…`}</span>
            <button type="button" onClick={() => setPaused(true)} className="inline-flex items-center gap-1 underline">
              <PauseCircle className="h-3.5 w-3.5" /> {es ? 'Quedarme aquí' : 'Stay here'}
            </button>
          </>
        )}
        {paused && !leaving && <span>{es ? 'Toca el botón cuando estés listo.' : 'Tap the button when you are ready.'}</span>}
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-center gap-4 border-t border-emerald-200 pt-3 text-xs">
        <a href={whatsappHref(lang)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-bold text-[#128C4A] hover:underline">
          <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
        </a>
        <a href={`mailto:${BRAND.email}`} className="inline-flex items-center gap-1 font-bold text-emerald-900 hover:underline">
          <Mail className="h-3.5 w-3.5" /> {BRAND.email}
        </a>
      </div>
    </div>
  );
};

export default HostedLeadCheckout;
